import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { api } from '@/api'
import { AdminPointListParams } from 'api'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { OffsetPagination } from '@/@diffrag/OffsetPagination'

export default function PointTransaction() {
  const page = useQueryValue('page', 1)
  const q = useQueryValue('q', undefined)

  const { data: pointRuleList } = useApi({
    id: 'pointTransactionList',
    fn: api().adminPointRule.adminPointRuleList,
  })

  const [selectedReferenceType, setSelectedReferenceType] = useState<
    string | undefined
  >(undefined)

  const [selectedType, setSelectedType] = useState<string | undefined>(
    undefined,
  )

  const { data, isFetching } = useApi({
    id: 'pointTransactionList',
    fn: api().adminPoint.adminPointList,
    params: {
      page,
      q,
      types: selectedType ? [selectedType] : [], // 선택된 타입
      referenceTypes: selectedReferenceType ? [selectedReferenceType] : [], // 선택된 참조 타입
    } as AdminPointListParams,
  })

  const referenceTypeOptions = useMemo(() => {
    const options = [{ label: '전체', value: undefined }]

    if (pointRuleList?.rows) {
      // 중복 제거를 위해 Set 사용
      const uniqueTypes = new Set()
      pointRuleList.rows.forEach((rule) => {
        if (rule.referenceType && !uniqueTypes.has(rule.referenceType)) {
          uniqueTypes.add(rule.referenceType)
          options.push({
            label: rule.name || rule.referenceType,
            value: rule.referenceType,
          })
        }
      })
    }

    return options
  }, [pointRuleList])

  const typeOptions = useMemo(() => {
    const options = [
      { label: '전체', value: undefined },
      { label: '적립', value: 'EARN' },
      { label: '사용/차감', value: 'USE' },
    ]

    return options
  }, [pointRuleList])

  const selectedReferenceTypeLabel = useMemo(() => {
    if (!selectedReferenceType) {
      return '전체'
    }
    return (
      referenceTypeOptions.find((item) => item.value === selectedReferenceType)
        ?.label || '전체'
    )
  }, [selectedReferenceType, referenceTypeOptions])

  const selectedTypeLabel = useMemo(() => {
    if (!selectedType) {
      return '전체'
    }
    return (
      typeOptions.find((item) => item.value === selectedType)?.label || '전체'
    )
  }, [selectedType, typeOptions])

  console.log(data)

  return (
    <ContentView>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">포인트명 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedReferenceTypeLabel}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-50 w-[200px] border bg-white shadow-lg"
            >
              {referenceTypeOptions.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value || 'all'}
                  checked={selectedReferenceType === option.value}
                  onCheckedChange={() => setSelectedReferenceType(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">구분 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedTypeLabel}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-50 w-[150px] border bg-white shadow-lg"
            >
              {typeOptions.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value || 'all'}
                  checked={selectedType === option.value}
                  onCheckedChange={() => setSelectedType(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <SimpleTable
        createHref="/point-transaction/edit"
        totalCount={data?.count || 0}
        columns={[
          {
            label: 'NO',
            key: 'id',
            isPrimary: true,
            href: (row) => `/point-transaction/edit?id=${row.id}`,
          },

          {
            label: '포인트명',
            key: 'referenceType',
            isPrimary: true,
            href: (row) => `/point-transaction/edit?id=${row.id}`,
            formatter: (value) => {
              return pointRuleList?.rows.find(
                (item) => item.referenceType === value,
              )?.name
            },
          },
          {
            label: '포인트',
            key: 'amount',
            formatter: (value, rawValue) => {
              const amount = Number(value)

              const color = amount >= 0 ? 'text-green-600' : 'text-red-600'
              const sign = amount >= 0 ? '+' : ''
              return (
                <span className={color}>
                  {sign}
                  {amount.toLocaleString()}P
                </span>
              )
            },
          },
          // {
          //   label: '구분',
          //   key: 'referenceType',
          //   valueMap: {
          //     SIGNUP: '회원가입',
          //     WRITE_PROFILE: '프로필 작성',
          //     ORDER: '주문',
          //     EVENT: '이벤트',
          //     REFERRAL: '추천',
          //     REVIEW: '리뷰 작성',
          //     COMMENT: '댓글 작성',
          //     DAILY: '일일 출석',
          //     EXPIRE: '포인트 만료',
          //     ADMIN: '관리자',
          //     COMMUNITY: '커뮤니티',
          //   },
          // },
          {
            label: '회원명',
            key: 'user',
            formatter: (value) => {
              return value?.userData?.nickname || value?.clinic?.name || '-'
            },
          },
          {
            label: '구분',
            key: 'transactionType',
            formatter: (value) => {
              switch (value) {
                case 'USE':
                  return '사용/차감'
                case 'RECOVER':
                  return '차감'
                default:
                  return '적립'
              }
            },
          },
          {
            label: '발생일시',
            key: 'createDate',
            formatter: (value) => {
              if (!value) return '-'
              return dayjs(value).format('YYYY-MM-DD HH:mm')
            },
          },
        ]}
        data={data?.rows || []}
        isLoading={isFetching}
        options={{
          disableNew: false, // 새 거래 등록 가능
          disableSort: true,
        }}
      />
      <OffsetPagination page={page} total={data?.count || 0} pageSize={20} />
    </ContentView>
  )
}
